import { CANCEL_REASONS, LEGAL_DOCUMENTS, formatPlanTokens, formatTry, type BillingOverviewDto, type CancelReason, type CheckoutStartDto, type ContractsDto, type SubscriptionDto } from '@dbrain/shared';
import { Badge, Button, Card, Dialog, Field, Select, Spinner } from '@dbrain/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { saveProfile, useBillingOverview, useBillingRefresh, usePayments } from '../../api/billing.js';
import { api } from '../../api/client.js';
import { useCompanyZone } from '../../api/hooks.js';
import { usePlan, usePlanCatalog, usePlanUsage } from '../../api/plan.js';
import { useCapabilities } from '../../experience.js';
import { emptyProfile, freeMonths, profileBody, ProfileFields, profileProblems, type ProfileForm } from '../../pages/bireysel/CheckoutPage.js';
import { HostedFormDialog } from '../billing/HostedFormDialog.js';
import { Markdown } from '../chat/Markdown.js';
import { ErrorAlert } from '../ErrorAlert.js';
import { usePlanOffer } from '../plan/PlanOffer.js';
import { UsageBar } from '../plan/PlanParts.js';

/**
 * Ayarlar › Abonelik (B2cAbonelik). Phase 16a: the plan and its use today. Phase 16c: the subscription (price, next
 * payment, card and "Kartı değiştir", "Yıllığa geç", "Aboneliği iptal et" with B2cIptal), this month's total for
 * information, the invoices ("Hazırlanıyor" until the operator enters them), the billing details and the two texts of
 * the purchase as mailed.
 */
export function SubscriptionSection() {
  const { t, i18n } = useTranslation();
  const plan = usePlan();
  const capabilities = useCapabilities();
  const usage = usePlanUsage({ refetchMs: 30_000 });
  const overview = useBillingOverview();
  const offer = usePlanOffer();
  const zone = useCompanyZone();
  const [cancelling, setCancelling] = useState(false);
  const [editing, setEditing] = useState(false);
  const [contracts, setContracts] = useState(false);
  if (!plan) return null;
  const locale = i18n.language;
  const date = (iso: string) => new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone: zone });
  const u = usage.data;
  const data = overview.data;
  const subscription = data?.subscription ?? null;
  return (
    <div className="subscription" data-testid="subscription-section">
      {subscription ? (
        <SubscriptionCard subscription={subscription} date={date} onCancel={() => setCancelling(true)} />
      ) : (
        <Card title={t('b2c.subscription.plan')}>
          <div className="subscription__plan">
            <span className="subscription__name" data-testid="subscription-plan">
              {plan.id === 'pro' && plan.proUntil ? t('b2c.subscription.proUntil', { date: date(plan.proUntil) }) : t('b2c.subscription.free', { plan: plan.name })}
            </span>
            <span className="subscription__state">{t('b2c.subscription.active')}</span>
          </div>
          {plan.id === 'pro' || !offer.canOffer ? null : (
            <div className="subscription__upgrade">
              <p className="muted">{data?.payments.open ? t('b2cBilling.subscription.upgradeOpen') : t('b2c.subscription.upgradeSoon')}</p>
              <Button onClick={offer.open} data-testid="subscription-upgrade">
                {t('b2c.subscription.upgrade')}
              </Button>
            </div>
          )}
        </Card>
      )}
      <Card title={t('b2c.subscription.usage')}>
        {!u ? (
          <Spinner label={t('b2c.subscription.loading')} />
        ) : (
          <dl className="subscription__rows subscription__rows--grid">
            <div className="subscription__row" data-testid="subscription-today">
              <dt>{t('b2c.subscription.today')}</dt>
              <dd>
                <span>{t('b2c.subscription.todayValue', { used: formatPlanTokens(u.today.used, locale), limit: formatPlanTokens(u.today.limit, locale) })}</span>
                <UsageBar used={u.today.used} limit={u.today.limit} label={t('b2c.subscription.today')} />
                <span className="muted">{t('b2c.subscription.reset')}</span>
              </dd>
            </div>
            <div className="subscription__row" data-testid="subscription-month">
              <dt>{t('b2cBilling.subscription.month')}</dt>
              <dd>
                <span>{t('b2cBilling.subscription.monthValue', { tokens: formatPlanTokens(u.month?.used ?? 0, locale) })}</span>
                <span className="muted">{t('b2cBilling.subscription.monthHint')}</span>
              </dd>
            </div>
            <div className="subscription__row" data-testid="subscription-documents">
              <dt>{t('b2c.subscription.documents')}</dt>
              <dd>
                <span>{t('b2c.subscription.documentsValue', { count: u.documents.count, limit: u.documents.limit.toLocaleString(locale) })}</span>
                <UsageBar used={u.documents.count} limit={u.documents.limit} label={t('b2c.subscription.documents')} />
                <span className="muted">{t('b2c.subscription.fileSize', { mb: plan.maxFileMb })}</span>
              </dd>
            </div>
            <div className="subscription__row" data-testid="subscription-connections">
              <dt>{t('b2c.subscription.connections')}</dt>
              <dd>
                <span>
                  {/* 16c.1: with a count, "2 / 3" */}
                  {u.connections.usable
                    ? u.connections.limit !== null && u.connections.limit !== undefined
                      ? t('b2cModels.connections.count', { used: u.connections.count, limit: u.connections.limit })
                      : t('b2c.subscription.connectionsOn', { count: u.connections.count })
                    : t('b2c.subscription.connectionsPro')}{' '}
                  · <Link to="/connectors">{t('b2c.subscription.connectionsLink')}</Link>
                </span>
              </dd>
            </div>
            {u.skills ? (
              <div className="subscription__row" data-testid="subscription-skills">
                <dt>{t('b2cModels.compare.ownSkills')}</dt>
                <dd>
                  <span>
                    {u.skills.limit === null ? t('b2cModels.skills.kept', { count: u.skills.count }) : t('b2cModels.skills.count', { used: u.skills.count, limit: u.skills.limit })} ·{' '}
                    <Link to="/skills">{t('skills.title')}</Link>
                  </span>
                </dd>
              </div>
            ) : null}
          </dl>
        )}
      </Card>
      {capabilities.billing && data && data.invoices.length > 0 ? <InvoicesCard data={data} date={date} onContracts={() => setContracts(true)} /> : null}
      {capabilities.billing && data?.profile ? (
        <Card
          title={t('b2cBilling.subscription.details')}
          actions={
            <Button variant="secondary" size="sm" onClick={() => setEditing(true)} data-testid="billing-profile-edit">
              {t('b2cBilling.subscription.edit')}
            </Button>
          }
        >
          <p className="subscription__profile" data-testid="billing-profile">
            {[data.profile.company ? data.profile.companyName : data.profile.fullName, `${data.profile.address}, ${data.profile.district} / ${data.profile.province}`].filter(Boolean).join(' · ')}
            {data.profile.company && data.profile.taxNumber ? <span className="muted"> · {t('b2cBilling.subscription.tax', { number: data.profile.taxNumber, office: data.profile.taxOffice })}</span> : null}
          </p>
        </Card>
      ) : null}
      {capabilities.billing ? (
        <p className="subscription__legal" data-testid="subscription-legal">
          <span>{t('b2cBilling.legalLinks')}:</span>{' '}
          {[LEGAL_DOCUMENTS.terms, LEGAL_DOCUMENTS.privacy, LEGAL_DOCUMENTS.cookies, LEGAL_DOCUMENTS.preInformation, LEGAL_DOCUMENTS.distanceSales].map((doc, index) => (
            <span key={doc.slug}>
              {index > 0 ? ' · ' : ''}
              <a href={`/yasal/${doc.slug}`} target="_blank" rel="noopener">
                {doc.title}
              </a>
            </span>
          ))}
        </p>
      ) : null}
      {cancelling && subscription ? <CancelDialog subscription={subscription} documents={u?.documents.count ?? 0} date={date} onClose={() => setCancelling(false)} /> : null}
      {editing && data?.profile ? <ProfileDialog data={data} onClose={() => setEditing(false)} /> : null}
      {contracts && data?.contracts ? <ContractsDialog orderId={data.contracts.orderId} onClose={() => setContracts(false)} /> : null}
    </div>
  );
}

function SubscriptionCard({ subscription, date, onCancel }: { subscription: SubscriptionDto; date: (iso: string) => string; onCancel: () => void }) {
  const { t, i18n } = useTranslation();
  const refresh = useBillingRefresh();
  const payments = usePayments();
  const pro = usePlanCatalog().data?.find((p) => p.id === 'pro');
  const [card, setCard] = useState<CheckoutStartDto | null>(null);
  const locale = i18n.language;
  const period = subscription.period;
  const yearly = useMutation({
    mutationFn: (to: 'monthly' | 'yearly') => api<SubscriptionDto>('PUT', '/api/billing/subscription/period', { period: to }),
    onSuccess: (next) => refresh(next),
  });
  const resume = useMutation({ mutationFn: () => api<SubscriptionDto>('POST', '/api/billing/subscription/resume', {}), onSuccess: (next) => refresh(next) });
  const changeCard = useMutation({ mutationFn: () => api<CheckoutStartDto>('POST', '/api/billing/card', {}), onSuccess: (started) => setCard(started) });
  const state = subscription.status === 'past_due' ? 'pastDue' : subscription.cancelAtPeriodEnd ? 'cancelled' : 'active';
  const months = pro ? freeMonths(pro) : 0;
  return (
    <Card title={t('b2c.subscription.plan')} className="subscription-card">
      <div className="subscription-card__body">
        <div className="subscription-card__main">
          <p className="subscription-card__name" data-testid="subscription-plan">
            {t(`b2cBilling.periods.pro.${period}`)}
            <Badge tone={state === 'active' ? 'success' : state === 'pastDue' ? 'danger' : 'warning'}>{t(`b2cBilling.subscription.state.${state}`)}</Badge>
          </p>
          <dl className="subscription-card__rows">
            <div>
              <dt>{t('b2cBilling.subscription.price')}</dt>
              <dd data-testid="subscription-price">{t(`b2cBilling.subscription.priceValue.${period}`, { price: formatTry(subscription.priceKurus, locale, true) })}</dd>
            </div>
            <div>
              <dt>{state === 'cancelled' ? t('b2cBilling.subscription.until') : state === 'pastDue' ? t('b2cBilling.subscription.retry') : t('b2cBilling.subscription.next')}</dt>
              <dd data-testid="subscription-next">
                {state === 'cancelled'
                  ? t('b2cBilling.subscription.untilValue', { date: date(subscription.currentPeriodEnd) })
                  : state === 'pastDue' && subscription.retry?.nextRetryAt
                    ? t('b2cBilling.subscription.retryValue', { date: date(subscription.retry.nextRetryAt), left: subscription.retry.left })
                    : subscription.nextPeriod
                      ? t('b2cBilling.subscription.switchValue', { date: date(subscription.currentPeriodEnd), price: formatTry(subscription.nextChargeKurus, locale) })
                      : t('b2cBilling.subscription.nextValue', { date: date(subscription.currentPeriodEnd), price: formatTry(subscription.nextChargeKurus, locale) })}
              </dd>
            </div>
            {subscription.card ? (
              <div>
                <dt>{t('b2cBilling.subscription.card')}</dt>
                <dd>
                  {t('b2cBilling.subscription.cardValue', { last4: subscription.card.last4 })}
                  {payments.open ? (
                    <>
                      {' · '}
                      <button type="button" className="link-button" onClick={() => changeCard.mutate()} disabled={changeCard.isPending} data-testid="subscription-change-card">
                        {t('b2cBilling.subscription.changeCard')}
                      </button>
                    </>
                  ) : null}
                </dd>
              </div>
            ) : null}
          </dl>
          {subscription.pendingTerms ? (
            <p className="subscription-card__pending" data-testid="subscription-pending">
              {t('b2cBilling.subscription.pending', {
                date: date(subscription.pendingTerms.from),
                price: formatTry(period === 'yearly' ? subscription.pendingTerms.terms.yearlyPriceKurus : subscription.pendingTerms.terms.monthlyPriceKurus, locale),
              })}
            </p>
          ) : null}
          <ErrorAlert error={yearly.error ?? resume.error ?? changeCard.error} />
        </div>
        <div className="subscription-card__actions">
          {state === 'cancelled' ? (
            <Button variant="secondary" onClick={() => resume.mutate()} busy={resume.isPending} data-testid="subscription-resume">
              {t('b2cBilling.subscription.resume')}
            </Button>
          ) : period === 'monthly' && state === 'active' ? (
            subscription.nextPeriod === 'yearly' ? (
              <Button variant="secondary" onClick={() => yearly.mutate('monthly')} busy={yearly.isPending} data-testid="subscription-stay-monthly">
                {t('b2cBilling.subscription.stayMonthly')}
              </Button>
            ) : (
              <Button variant="secondary" onClick={() => yearly.mutate('yearly')} busy={yearly.isPending} data-testid="subscription-yearly">
                {months > 0 ? t('b2cBilling.subscription.toYearlyFree', { count: months }) : t('b2cBilling.subscription.toYearly')}
              </Button>
            )
          ) : null}
          {state !== 'cancelled' ? (
            <button type="button" className="link-button link-button--danger" onClick={onCancel} data-testid="subscription-cancel">
              {t('b2cBilling.subscription.cancel')}
            </button>
          ) : null}
        </div>
      </div>
      {card ? (
        <HostedFormDialog
          checkout={card}
          description={t('b2cBilling.subscription.cardForm')}
          onClose={() => setCard(null)}
          onFailed={() => setCard(null)}
          onPaid={() => {
            setCard(null);
            refresh();
          }}
        />
      ) : null}
    </Card>
  );
}

function InvoicesCard({ data, date, onContracts }: { data: BillingOverviewDto; date: (iso: string) => string; onContracts: () => void }) {
  const { t, i18n } = useTranslation();
  return (
    <Card title={t('b2cBilling.subscription.invoices')}>
      <div className="invoice-table-wrap">
        <table className="invoice-table" data-testid="invoices">
          <thead>
            <tr>
              <th scope="col">{t('b2cBilling.invoices.date')}</th>
              <th scope="col">{t('b2cBilling.invoices.description')}</th>
              <th scope="col" className="invoice-table__amount">
                {t('b2cBilling.invoices.amount')}
              </th>
              <th scope="col">{t('b2cBilling.invoices.payment')}</th>
              <th scope="col">{t('b2cBilling.invoices.number')}</th>
              <th scope="col">
                <span className="sr-only">{t('b2cBilling.invoices.download')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {data.invoices.map((invoice) => (
              <tr key={invoice.id} data-testid={`invoice-${invoice.id}`}>
                <td>{date(invoice.date)}</td>
                <td>{invoice.description}</td>
                <td className="invoice-table__amount">{formatTry(invoice.amountKurus, i18n.language)}</td>
                <td>
                  <Badge tone={invoice.paymentStatus === 'paid' ? 'success' : 'neutral'}>{t(`b2cBilling.invoices.status.${invoice.paymentStatus}`)}</Badge>
                </td>
                <td>{invoice.status === 'issued' && invoice.number ? <span className="invoice-table__number">{invoice.number}</span> : <span className="invoice-table__pending">{t('b2cBilling.invoices.preparing')}</span>}</td>
                <td className="invoice-table__download">
                  {invoice.status === 'issued' && invoice.hasPdf ? (
                    <a href={`/api/billing/invoices/${invoice.id}/pdf`} download data-testid="invoice-pdf">
                      {t('b2cBilling.invoices.pdf')}
                    </a>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.contracts ? (
        <p className="subscription__contracts">
          {t('b2cBilling.subscription.contractsLine', { date: date(data.contracts.date) })}{' '}
          <button type="button" className="link-button" onClick={onContracts} data-testid="subscription-contracts">
            {t('b2cBilling.subscription.contractsOpen')}
          </button>
        </p>
      ) : null}
    </Card>
  );
}

/** B2cIptal: what Ücretsiz keeps and closes, an optional reason, "Pro'da kal" or "Aboneliği iptal et" */
function CancelDialog({ subscription, documents, date, onClose }: { subscription: SubscriptionDto; documents: number; date: (iso: string) => string; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const refresh = useBillingRefresh();
  const free = usePlanCatalog().data?.find((p) => p.id === 'free');
  const [reason, setReason] = useState<CancelReason | ''>('');
  const cancel = useMutation({
    mutationFn: () => api<SubscriptionDto>('POST', '/api/billing/subscription/cancel', { reason: reason || null }),
    onSuccess: (next) => {
      refresh(next);
      onClose();
    },
  });
  return (
    <Dialog
      title={t('b2cBilling.cancel.title')}
      onClose={onClose}
      closeLabel={t('common.close')}
      size="md"
      role="alertdialog"
      data-testid="cancel-dialog"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} data-testid="cancel-stay">
            {t('b2cBilling.cancel.stay')}
          </Button>
          <Button variant="danger" onClick={() => cancel.mutate()} busy={cancel.isPending} data-testid="cancel-confirm">
            {t('b2cBilling.cancel.confirm')}
          </Button>
        </>
      }
    >
      <p className="cancel-dialog__lead">{t('b2cBilling.cancel.lead', { date: date(subscription.currentPeriodEnd) })}</p>
      {free ? (
        <div className="cancel-dialog__free">
          <p className="cancel-dialog__free-title">{t('b2cBilling.cancel.onFree', { plan: free.name })}</p>
          <ul>
            <li>{t('b2cBilling.cancel.tokens', { tokens: formatPlanTokens(free.dailyTokens, i18n.language) })}</li>
            <li>{documents > free.maxDocuments ? t('b2cBilling.cancel.documentsOver', { count: documents, limit: free.maxDocuments }) : t('b2cBilling.cancel.documents')}</li>
            <li>{t('b2cBilling.cancel.connections')}</li>
            <li>{t('b2cBilling.cancel.files', { mb: free.maxFileMb })}</li>
          </ul>
        </div>
      ) : null}
      <Field label={<>{t('b2cBilling.cancel.reason')} <span className="muted">{t('b2cBilling.checkout.optional')}</span></>}>
        {(f) => (
          <Select id={f.id} aria-describedby={f.describedBy} aria-invalid={f.invalid || undefined} value={reason} onChange={(e) => setReason(e.target.value as CancelReason | '')} data-testid="cancel-reason">
            <option value="">{t('b2cBilling.cancel.choose')}</option>
            {CANCEL_REASONS.map((r) => (
              <option key={r} value={r}>
                {t(`b2cBilling.cancel.reasons.${r}`)}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <ErrorAlert error={cancel.error} />
    </Dialog>
  );
}

/** "Düzenle": the billing details for the next invoices */
function ProfileDialog({ data, onClose }: { data: BillingOverviewDto; onClose: () => void }) {
  const { t } = useTranslation();
  const refresh = useBillingRefresh();
  const p = data.profile;
  const [form, setForm] = useState<ProfileForm>(
    p ? { fullName: p.fullName, nationalId: '', province: p.province, district: p.district, address: p.address, company: p.company, companyName: p.companyName ?? '', taxNumber: p.taxNumber ?? '', taxOffice: p.taxOffice ?? '' } : emptyProfile,
  );
  const [changed, setChanged] = useState(false);
  const [tried, setTried] = useState(false);
  const problems = profileProblems(form);
  const save = useMutation({
    mutationFn: () => saveProfile(profileBody(form, changed)),
    onSuccess: () => {
      refresh();
      onClose();
    },
  });
  return (
    <Dialog
      title={t('b2cBilling.subscription.details')}
      onClose={onClose}
      closeLabel={t('common.close')}
      size="wide"
      data-testid="billing-profile-dialog"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => {
              setTried(true);
              if (Object.keys(problems).length === 0) save.mutate();
            }}
            busy={save.isPending}
            data-testid="billing-profile-save"
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <p className="muted">{t('b2cBilling.subscription.detailsHint')}</p>
      <ProfileFields
        form={form}
        onChange={(next) => {
          if ('nationalId' in next) setChanged(true);
          setForm((current) => ({ ...current, ...next }));
        }}
        problems={problems}
        nationalIdHint={changed ? null : (p?.nationalIdHint ?? null)}
        show={tried}
      />
      <ErrorAlert error={save.error} />
    </Dialog>
  );
}

/** The Ön Bilgilendirme Formu and the Mesafeli Satış Sözleşmesi as accepted and mailed */
function ContractsDialog({ orderId, onClose }: { orderId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const contracts = useQuery({ queryKey: ['billing', 'contracts', orderId], queryFn: () => api<ContractsDto>('GET', `/api/billing/contracts/${orderId}`) });
  const body = (text: string) => text.replace(/^#[ \t]+[^\n]*\n+/, '');
  return (
    <Dialog title={t('b2cBilling.subscription.contractsTitle')} onClose={onClose} closeLabel={t('common.close')} size="wide" dismissOnBackdrop data-testid="contracts-dialog">
      <ErrorAlert error={contracts.error} />
      {contracts.data ? (
        <div className="contracts" lang="tr">
          {[contracts.data.preInformation, contracts.data.distanceSales].map((doc) => (
            <section key={doc.title} className="contracts__doc">
              <h3 className="contracts__title">{doc.title}</h3>
              <p className="muted">{t('b2cAuth.legal.version', { version: doc.version })}</p>
              <Markdown source={body(doc.text)} className="legal-draft__body" />
            </section>
          ))}
        </div>
      ) : (
        <Spinner label={t('b2c.subscription.loading')} />
      )}
    </Dialog>
  );
}

